import { ref } from 'vue';

import { defineStore } from 'pinia';

import debounce from 'lodash/debounce';

import { getUserSettingsByPage, upsertUserSetting } from '@/services/api/userSettingsApi';
import storage from '@/services/storage';

const LEGACY_LS_KEY = 'is-blur-app';
const LS_KEY = 'wiseppc-blur-settings';
const SETTINGS_PAGE = 'profile';
const SETTINGS_KEY = 'blur-fields';

const FIELD_KEYS = [
  'campaigns',
  'targets',
  'adGroups',
  'searchTerms',
  // Shopify
  'products', // product / variant titles, SKUs, barcodes, vendors, Amazon ASINs, thumbnails
  'orders', // order names, discount codes, landing pages, referrers, UTM text
  'customers', // names, emails, phones, addresses
  'collections',
];

// Fields whose plain grid columns are blurred through a CSS class instead of a Vue computed
// (`.olap-blur-private--<key>` in main.scss, whose @each list must match this one). The class is
// reactive through `<html>`: every switched-OFF field puts `blur-off-<key>` there, so a cell never
// needs to re-render when a sub-toggle flips.
export const CSS_BLUR_FIELDS = ['products', 'orders', 'customers', 'collections'];
const OFF_CLASS_PREFIX = 'blur-off-';

const buildDefaultFields = () =>
  FIELD_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

const sanitizeFields = (raw) => {
  const base = buildDefaultFields();
  if (!raw || typeof raw !== 'object') return base;
  FIELD_KEYS.forEach((key) => {
    if (typeof raw[key] === 'boolean') base[key] = raw[key];
  });
  return base;
};

export const useBlurStore = defineStore('blur', () => {
  const userBlur = ref(false);
  const fields = ref(buildDefaultFields());

  const applyHtmlClass = (value) => {
    if (value) {
      document.documentElement.classList.add('blur');
    } else {
      document.documentElement.classList.remove('blur');
    }
  };

  const applyFieldClasses = () => {
    CSS_BLUR_FIELDS.forEach((key) => {
      document.documentElement.classList.toggle(`${OFF_CLASS_PREFIX}${key}`, !fields.value[key]);
    });
  };

  const persistLocal = () => {
    storage.set(LS_KEY, {
      enabled: userBlur.value,
      fields: { ...fields.value },
    });
  };

  const persistServer = debounce(() => {
    upsertUserSetting({
      page_name: SETTINGS_PAGE,
      key: SETTINGS_KEY,
      value: {
        enabled: userBlur.value,
        fields: { ...fields.value },
      },
      is_personal: true,
    }).catch((e) => {
      console.warn('[blurStore] Server save failed (kept in localStorage):', e);
    });
  }, 800);

  const persist = () => {
    persistLocal();
    persistServer();
  };

  const setBlur = (blur) => {
    userBlur.value = !!blur;
    applyHtmlClass(userBlur.value);
    persist();
  };

  const toggleBlur = () => setBlur(!userBlur.value);

  const setField = (field, value) => {
    if (!FIELD_KEYS.includes(field)) return;
    fields.value = { ...fields.value, [field]: !!value };
    applyFieldClasses();
    persist();
  };

  // The master switch AND the field's sub-toggle (what useBlurForField wraps in a computed).
  const isFieldBlurred = (field) => !!userBlur.value && !!fields.value[field];

  const toggleField = (field) => setField(field, !fields.value[field]);

  const resetFields = () => {
    fields.value = buildDefaultFields();
    applyFieldClasses();
    persist();
  };

  const hydrateFromValue = (value) => {
    userBlur.value = !!value?.enabled;
    fields.value = sanitizeFields(value?.fields);
    applyHtmlClass(userBlur.value);
    applyFieldClasses();
  };

  // Dedup: only hit the server once per app token. Reset on failure so a later trigger retries.
  let lastLoadedToken = null;

  const initializeBlur = async () => {
    const cached = storage.get(LS_KEY);
    if (cached && typeof cached === 'object') {
      hydrateFromValue(cached);
    } else {
      const legacy = storage.get(LEGACY_LS_KEY);
      hydrateFromValue({ enabled: legacy === true, fields: buildDefaultFields() });
      persistLocal();
    }

    // Server load needs an app token. Skip (use localStorage) until one exists — App.vue
    // re-invokes this via an accessToken watcher — and don't refetch for the same token.
    const { useAuthStore } = await import('@/stores/auth');
    const token = useAuthStore().accessToken;
    if (!token || token === lastLoadedToken) return;
    lastLoadedToken = token;

    try {
      const res = await getUserSettingsByPage(SETTINGS_PAGE, { key: SETTINGS_KEY });
      const items = res?.data?.items || [];
      if (items.length > 0 && items[0].value && typeof items[0].value === 'object') {
        hydrateFromValue(items[0].value);
        persistLocal();
      }
    } catch (e) {
      lastLoadedToken = null; // allow retry on the next trigger
      console.warn('[blurStore] Server load failed (using localStorage):', e);
    }
  };

  return {
    userBlur,
    fields,
    toggleBlur,
    setBlur,
    setField,
    toggleField,
    isFieldBlurred,
    resetFields,
    initializeBlur,
  };
});
